import React, { useEffect, useMemo, useState } from "react";
import {
  deleteAdminBoard,
  getAdminBoardById,
  getAdminBoards,
  removeAdminBoardImage,
  saveAdminBoardAllLanguages,
  uploadAdminBoardImage,
} from "../../../api/boardsAdmin";
import AdminProductPdfsPanel from "./AdminProductPdfsPanel";
import AdminEditorModal from "./AdminEditorModal";
import AdminCollapsibleSection from "./AdminCollapsibleSection";
import AdminProductListCard from "./AdminProductListCard";
import { resolveMediaUrl } from "../../../utils/media";
import { btnAccent, btnDanger, btnPrimary, btnSecondary, langTabActive, langTabIdle } from "./adminProductUi";
import { DashboardReloadButton } from "./dashboardUi";
import { useNotification } from "../../../context/NotificationContext";

const LANGS = ["en", "ar", "ku"];
const LANG_LABELS = { en: "English", ar: "Arabic", ku: "Kurdish" };

const normLang = (value) =>
  String(value || "en")
    .toLowerCase()
    .split("-")[0];

const normType = (value) => String(value || "").trim().toLowerCase();

/** All translatable content for a single language variant of a board. */
const emptyLangFields = () => ({
  title: "",
  heroDescription: "",
  recommendationsIntro: "",
  keyFeatures: [""],
  specificationSections: [
    { heading: "", displayOrder: 1, items: [{ label: "", value: "", displayOrder: 1 }] },
  ],
  recommendationApplications: [{ numberLabel: "01", text: "" }],
  recommendationAccordionItems: [{ title: "", displayOrder: 1, bullets: [{ text: "", displayOrder: 1 }] }],
  sustainabilityCards: [{ title: "", text: "", iconUrl: "" }],
  sustainabilityStorageItems: [{ text: "" }],
});

const emptyTranslations = () => ({
  en: emptyLangFields(),
  ar: emptyLangFields(),
  ku: emptyLangFields(),
});

const EMPTY_FORM = {
  boardType: "",
  imageUrl: "",
  isActive: true,
  translations: emptyTranslations(),
  ids: {},
  primaryId: null,
};

const getBoardName = (board) =>
  board?.title || board?.name || board?.heroTitle || board?.slug || board?.id || "Board";

/** Map a single DB board row into the per-language form field shape. */
const mapRowToLangFields = (board) => {
  const keyFeatures = Array.isArray(board?.keyFeatures)
    ? board.keyFeatures
        .map((f) => (typeof f === "string" ? f : f?.text || f?.title || ""))
        .filter(Boolean)
    : [];

  const specificationSections = Array.isArray(board?.specificationSections)
    ? board.specificationSections.map((s, sectionIdx) => ({
        heading: s?.heading || s?.title || s?.name || "",
        displayOrder: Number(s?.displayOrder) || sectionIdx + 1,
        items: Array.isArray(s?.items) && s.items.length
          ? s.items.map((it, itemIdx) => ({
              label: it?.label || "",
              value: it?.value || "",
              displayOrder: Number(it?.displayOrder) || itemIdx + 1,
            }))
          : [{ label: "", value: "", displayOrder: 1 }],
      }))
    : [];

  const recommendationApplications = Array.isArray(board?.recommendationApplications)
    ? board.recommendationApplications.map((r, idx) =>
        typeof r === "string"
          ? { numberLabel: String(idx + 1).padStart(2, "0"), text: r }
          : {
              numberLabel: r?.numberLabel || String(idx + 1).padStart(2, "0"),
              text: r?.text || r?.body || "",
            }
      )
    : [];

  const recommendationAccordionItems = Array.isArray(board?.recommendationAccordionItems)
    ? board.recommendationAccordionItems.map((a, idx) => ({
        title: a?.title || "",
        displayOrder: Number(a?.displayOrder) || idx + 1,
        bullets: Array.isArray(a?.bullets) && a.bullets.length
          ? a.bullets.map((b, bIdx) => ({
              text: b?.text || "",
              displayOrder: Number(b?.displayOrder) || bIdx + 1,
            }))
          : [{ text: "", displayOrder: 1 }],
      }))
    : [];

  const sustainabilityCards = Array.isArray(board?.sustainabilityCards)
    ? board.sustainabilityCards.map((c) => ({
        title: c?.title || "",
        text: c?.text || c?.body || "",
        iconUrl: c?.iconUrl || c?.icon || "",
      }))
    : [];

  const sustainabilityStorageItems = Array.isArray(board?.sustainabilityStorageItems)
    ? board.sustainabilityStorageItems.map((i) =>
        typeof i === "string" ? { text: i } : { text: i?.text || i?.body || "" }
      )
    : [];

  const fallback = emptyLangFields();
  return {
    title: board?.name || board?.title || "",
    heroDescription: board?.heroDescription || board?.description || "",
    recommendationsIntro: board?.recommendationsIntro || "",
    keyFeatures: keyFeatures.length ? keyFeatures : fallback.keyFeatures,
    specificationSections: specificationSections.length
      ? specificationSections
      : fallback.specificationSections,
    recommendationApplications: recommendationApplications.length
      ? recommendationApplications
      : fallback.recommendationApplications,
    recommendationAccordionItems: recommendationAccordionItems.length
      ? recommendationAccordionItems
      : fallback.recommendationAccordionItems,
    sustainabilityCards: sustainabilityCards.length ? sustainabilityCards : fallback.sustainabilityCards,
    sustainabilityStorageItems: sustainabilityStorageItems.length
      ? sustainabilityStorageItems
      : fallback.sustainabilityStorageItems,
  };
};

/** Build a single form (with all languages) from the sibling rows of one board type. */
const mapBoardsToForm = (rows) => {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const primary = list.find((r) => normLang(r?.language) === "en") || list[0] || {};
  const translations = emptyTranslations();
  const ids = {};
  list.forEach((row) => {
    const lang = normLang(row?.language);
    if (!LANGS.includes(lang)) return;
    translations[lang] = mapRowToLangFields(row);
    ids[lang] = row?.id ?? null;
  });
  return {
    boardType: primary?.boardType || primary?.type || "",
    imageUrl: primary?.imageUrl || list.find((r) => r?.imageUrl)?.imageUrl || "",
    isActive: list.length ? list.every((r) => r?.isActive !== false) : true,
    translations,
    ids,
    primaryId: primary?.id ?? list[0]?.id ?? null,
  };
};

/** Convert one language's form fields into a backend language-content payload. */
const toLanguageContent = (lang, fields) => ({
  language: lang,
  name: (fields.title || "").trim(),
  heroDescription: (fields.heroDescription || "").trim(),
  recommendationsIntro: (fields.recommendationsIntro || "").trim(),
  keyFeatures: (fields.keyFeatures || [])
    .map((text) => (text || "").trim())
    .filter(Boolean)
    .map((text) => ({ text })),
  specificationSections: (fields.specificationSections || [])
    .map((s) => ({
      heading: (s.heading || "").trim(),
      items: (s.items || [])
        .filter((it) => (it.label || "").trim() && (it.value || "").trim())
        .map((it) => ({ label: it.label.trim(), value: it.value.trim() })),
    }))
    .filter((s) => s.heading),
  recommendationApplications: (fields.recommendationApplications || [])
    .map((app, idx) => ({
      numberLabel: (app?.numberLabel || String(idx + 1).padStart(2, "0")).trim(),
      text: (app?.text || "").trim(),
    }))
    .filter((app) => app.text),
  recommendationAccordionItems: (fields.recommendationAccordionItems || [])
    .map((a) => ({
      title: (a.title || "").trim(),
      bullets: (a.bullets || [])
        .filter((b) => (b.text || "").trim())
        .map((b) => ({ text: b.text.trim() })),
    }))
    .filter((a) => a.title),
  sustainabilityCards: (fields.sustainabilityCards || [])
    .filter((c) => (c.title || "").trim() && (c.text || "").trim())
    .map((c) => ({ title: c.title.trim(), text: c.text.trim(), iconUrl: (c.iconUrl || "").trim() })),
  sustainabilityStorageItems: (fields.sustainabilityStorageItems || [])
    .map((item) => (typeof item === "string" ? item : item?.text || ""))
    .map((text) => (text || "").trim())
    .filter(Boolean)
    .map((text) => ({ text })),
});

/** Build the multi-language save payload. Languages without a title are omitted. */
const buildSavePayload = (form) => ({
  boardType: (form.boardType || "").trim(),
  imageUrl: (form.imageUrl || "").trim() || null,
  isActive: Boolean(form.isActive),
  languages: LANGS.filter((lang) => (form.translations?.[lang]?.title || "").trim()).map((lang) =>
    toLanguageContent(lang, form.translations[lang])
  ),
});

const BoardsManager = () => {
  const { notify } = useNotification();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [editingBoardType, setEditingBoardType] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [activeCreateLang, setActiveCreateLang] = useState("en");
  const [activeEditLang, setActiveEditLang] = useState("en");
  const [createImageFile, setCreateImageFile] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadBoards = async () => {
    setLoading(true);
    try {
      const data = await getAdminBoards();
      setBoards(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load boards",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBoards();
  }, []);

  // One card per board type; each card bundles all language variants.
  const groupedBoards = useMemo(() => {
    const groups = new Map();
    boards.forEach((board) => {
      const key = normType(board?.boardType || board?.type) || `#${board?.id}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(board);
    });
    return [...groups.values()]
      .map((rows) => {
        const rep = rows.find((r) => normLang(r?.language) === "en") || rows[0];
        return {
          key: normType(rep?.boardType || rep?.type) || `#${rep?.id}`,
          rep,
          rows,
          languages: LANGS.filter((lang) => rows.some((r) => normLang(r?.language) === lang)),
        };
      })
      .sort((a, b) => Number(a?.rep?.id || 0) - Number(b?.rep?.id || 0));
  }, [boards]);

  const refreshBoardType = async (boardType) => {
    await loadBoards();
    if (boardType) {
      const siblings = (await getAdminBoards().catch(() => [])).filter(
        (b) => normType(b?.boardType || b?.type) === normType(boardType)
      );
      const fresh = await Promise.all(
        siblings.map((s) => getAdminBoardById(s.id).catch(() => s))
      );
      const mapped = mapBoardsToForm(fresh);
      setEditForm(mapped);
    }
  };

  const handleCreate = async () => {
    const anyTitle = LANGS.some((lang) => (createForm.translations[lang].title || "").trim());
    if (!anyTitle || !createForm.boardType.trim()) {
      notify({ type: "error", title: "Board Type and at least one Title are required" });
      return;
    }
    setCreating(true);
    try {
      const payload = buildSavePayload(createForm);
      const savedRows = await saveAdminBoardAllLanguages(payload);

      if (createImageFile && Array.isArray(savedRows) && savedRows.length) {
        const rep = savedRows.find((b) => normLang(b?.language) === "en") || savedRows[0];
        if (rep?.id) {
          await uploadAdminBoardImage({ id: rep.id, file: createImageFile });
        } else {
          notify({
            type: "error",
            title: "Image upload skipped",
            message: "Board saved but image could not be uploaded automatically.",
          });
        }
      }

      notify({ type: "success", title: "Board saved for all languages" });
      setCreateForm(EMPTY_FORM);
      setCreateImageFile(null);
      setActiveCreateLang("en");
      await loadBoards();
    } catch (error) {
      notify({
        type: "error",
        title: "Save failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = async (group) => {
    const boardType = normType(group?.rep?.boardType || group?.rep?.type);
    setEditingBoardType(boardType || group.key);
    setEditImageFile(null);
    setActiveEditLang("en");
    setEditForm(mapBoardsToForm(group.rows));
    try {
      const fresh = await Promise.all(
        group.rows.map((row) => getAdminBoardById(row.id).catch(() => row))
      );
      setEditForm(mapBoardsToForm(fresh));
    } catch {
      /* keep list rows */
    }
  };

  const saveEdit = async () => {
    if (!editingBoardType) return;
    const anyTitle = LANGS.some((lang) => (editForm.translations[lang].title || "").trim());
    if (!anyTitle || !editForm.boardType.trim()) {
      notify({ type: "error", title: "Board Type and at least one Title are required" });
      return;
    }
    setSaving(true);
    try {
      const payload = buildSavePayload(editForm);
      const savedRows = await saveAdminBoardAllLanguages(payload);
      if (editImageFile) {
        const rep =
          (Array.isArray(savedRows) &&
            (savedRows.find((b) => normLang(b?.language) === "en") || savedRows[0])) ||
          null;
        if (rep?.id) {
          await uploadAdminBoardImage({ id: rep.id, file: editImageFile });
        }
      }
      notify({ type: "success", title: "Board updated for all languages" });
      setEditingBoardType(null);
      setEditForm(EMPTY_FORM);
      setEditImageFile(null);
      await loadBoards();
    } catch (error) {
      notify({
        type: "error",
        title: "Update failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    const ok = window.confirm(
      `Delete board "${getBoardName(group?.rep)}" and all its language versions?`
    );
    if (!ok) return;
    try {
      for (const row of group.rows) {
        // eslint-disable-next-line no-await-in-loop
        await deleteAdminBoard({ id: row.id });
      }
      notify({ type: "success", title: "Board deleted" });
      await loadBoards();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const closeEdit = () => {
    setEditingBoardType(null);
    setEditForm(EMPTY_FORM);
    setEditImageFile(null);
    setActiveEditLang("en");
  };

  const editRepId = editForm.ids?.en ?? editForm.primaryId ?? null;

  // Shared per-language content editor used by both create and edit forms.
  const renderLangContent = (form, setForm, lang) => {
    const fields = form.translations[lang] || emptyLangFields();

    const patch = (partial) =>
      setForm((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: { ...prev.translations[lang], ...partial },
        },
      }));

    const setArray = (key, updater) =>
      setForm((prev) => {
        const current = prev.translations[lang][key] || [];
        const next = updater([...current]);
        return {
          ...prev,
          translations: {
            ...prev.translations,
            [lang]: { ...prev.translations[lang], [key]: next },
          },
        };
      });

    const dir = lang === "ar" || lang === "ku" ? "rtl" : "ltr";

    return (
      <div className="grid gap-4" dir={dir}>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm text-slate-700 sm:col-span-2">
            Title ({lang.toUpperCase()})
            <input
              type="text"
              value={fields.title}
              onChange={(e) => patch({ title: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              placeholder="Regular Light Weight"
            />
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">
            Hero Description ({lang.toUpperCase()})
            <textarea
              value={fields.heroDescription}
              onChange={(e) => patch({ heroDescription: e.target.value })}
              className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              placeholder="Board hero description"
            />
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">
            Recommendations Intro ({lang.toUpperCase()})
            <textarea
              value={fields.recommendationsIntro}
              onChange={(e) => patch({ recommendationsIntro: e.target.value })}
              className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
          </label>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Key Features ({lang.toUpperCase()})</p>
          {fields.keyFeatures.map((item, idx) => (
            <div key={`kf-${lang}-${idx}`} className="flex gap-2 mb-2">
              <input
                type="text"
                value={item}
                onChange={(e) =>
                  setArray("keyFeatures", (next) => {
                    next[idx] = e.target.value;
                    return next;
                  })
                }
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => setArray("keyFeatures", (next) => next.filter((_, i) => i !== idx))}
                className="px-3 rounded border border-rose-200 text-rose-700"
              >
                -
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setArray("keyFeatures", (next) => [...next, ""])}
            className="text-xs px-3 py-1.5 rounded border border-slate-300"
          >
            Add feature
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Specifications ({lang.toUpperCase()})</p>
          {fields.specificationSections.map((section, idx) => (
            <div key={`sp-${lang}-${idx}`} className="mb-3 rounded-lg border border-slate-200 p-2">
              <div className="grid sm:grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  value={section.heading}
                  onChange={(e) =>
                    setArray("specificationSections", (next) => {
                      next[idx] = { ...next[idx], heading: e.target.value };
                      return next;
                    })
                  }
                  placeholder="Section heading"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="number"
                  value={section.displayOrder}
                  onChange={(e) =>
                    setArray("specificationSections", (next) => {
                      next[idx] = { ...next[idx], displayOrder: Number(e.target.value) || idx + 1 };
                      return next;
                    })
                  }
                  placeholder="Display order"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              {(section.items || []).map((specItem, itemIdx) => (
                <div key={`sp-item-${lang}-${idx}-${itemIdx}`} className="grid sm:grid-cols-3 gap-2 mb-2">
                  <input
                    type="text"
                    value={specItem.label}
                    onChange={(e) =>
                      setArray("specificationSections", (next) => {
                        const items = [...(next[idx].items || [])];
                        items[itemIdx] = { ...items[itemIdx], label: e.target.value };
                        next[idx] = { ...next[idx], items };
                        return next;
                      })
                    }
                    placeholder="Label"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    value={specItem.value}
                    onChange={(e) =>
                      setArray("specificationSections", (next) => {
                        const items = [...(next[idx].items || [])];
                        items[itemIdx] = { ...items[itemIdx], value: e.target.value };
                        next[idx] = { ...next[idx], items };
                        return next;
                      })
                    }
                    placeholder="Value"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <input
                    type="number"
                    value={specItem.displayOrder}
                    onChange={(e) =>
                      setArray("specificationSections", (next) => {
                        const items = [...(next[idx].items || [])];
                        items[itemIdx] = {
                          ...items[itemIdx],
                          displayOrder: Number(e.target.value) || itemIdx + 1,
                        };
                        next[idx] = { ...next[idx], items };
                        return next;
                      })
                    }
                    placeholder="Order"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
              ))}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setArray("specificationSections", (next) => {
                      const items = [
                        ...(next[idx].items || []),
                        { label: "", value: "", displayOrder: (next[idx].items?.length || 0) + 1 },
                      ];
                      next[idx] = { ...next[idx], items };
                      return next;
                    })
                  }
                  className="text-xs px-2 py-1 rounded border border-slate-300"
                >
                  Add spec item
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setArray("specificationSections", (next) => next.filter((_, i) => i !== idx))
                  }
                  className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-700"
                >
                  Remove section
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setArray("specificationSections", (next) => [
                ...next,
                { heading: "", displayOrder: next.length + 1, items: [{ label: "", value: "", displayOrder: 1 }] },
              ])
            }
            className="text-xs px-3 py-1.5 rounded border border-slate-300"
          >
            Add specification section
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">
            Recommendation Applications ({lang.toUpperCase()})
          </p>
          {fields.recommendationApplications.map((item, idx) => (
            <div key={`ra-${lang}-${idx}`} className="grid sm:grid-cols-12 gap-2 mb-2">
              <input
                type="text"
                value={item?.numberLabel || ""}
                onChange={(e) =>
                  setArray("recommendationApplications", (next) => {
                    next[idx] = { ...(next[idx] || {}), numberLabel: e.target.value };
                    return next;
                  })
                }
                placeholder="01"
                className="sm:col-span-3 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                type="text"
                value={item?.text || ""}
                onChange={(e) =>
                  setArray("recommendationApplications", (next) => {
                    next[idx] = { ...(next[idx] || {}), text: e.target.value };
                    return next;
                  })
                }
                placeholder="Application text"
                className="sm:col-span-8 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() =>
                  setArray("recommendationApplications", (next) => next.filter((_, i) => i !== idx))
                }
                className="px-3 rounded border border-rose-200 text-rose-700"
              >
                -
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setArray("recommendationApplications", (next) => [
                ...next,
                { numberLabel: String(next.length + 1).padStart(2, "0"), text: "" },
              ])
            }
            className="text-xs px-3 py-1.5 rounded border border-slate-300"
          >
            Add application
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">
            Recommendation Accordion Items ({lang.toUpperCase()})
          </p>
          {fields.recommendationAccordionItems.map((item, idx) => (
            <div key={`rai-${lang}-${idx}`} className="mb-3 rounded-lg border border-slate-200 p-2">
              <div className="grid sm:grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) =>
                    setArray("recommendationAccordionItems", (next) => {
                      next[idx] = { ...next[idx], title: e.target.value };
                      return next;
                    })
                  }
                  placeholder="Title"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="number"
                  value={item.displayOrder}
                  onChange={(e) =>
                    setArray("recommendationAccordionItems", (next) => {
                      next[idx] = { ...next[idx], displayOrder: Number(e.target.value) || idx + 1 };
                      return next;
                    })
                  }
                  placeholder="Order"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              {(item.bullets || []).map((bullet, bIdx) => (
                <div key={`rai-b-${lang}-${idx}-${bIdx}`} className="grid sm:grid-cols-12 gap-2 mb-2">
                  <input
                    type="text"
                    value={bullet.text}
                    onChange={(e) =>
                      setArray("recommendationAccordionItems", (next) => {
                        const bullets = [...(next[idx].bullets || [])];
                        bullets[bIdx] = { ...bullets[bIdx], text: e.target.value };
                        next[idx] = { ...next[idx], bullets };
                        return next;
                      })
                    }
                    placeholder="Bullet text"
                    className="sm:col-span-11 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setArray("recommendationAccordionItems", (next) => {
                        const bullets = (next[idx].bullets || []).filter((_, i) => i !== bIdx);
                        next[idx] = { ...next[idx], bullets };
                        return next;
                      })
                    }
                    className="px-3 rounded border border-rose-200 text-rose-700"
                  >
                    -
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setArray("recommendationAccordionItems", (next) => {
                      next[idx] = {
                        ...next[idx],
                        bullets: [
                          ...(next[idx].bullets || []),
                          { text: "", displayOrder: (next[idx].bullets?.length || 0) + 1 },
                        ],
                      };
                      return next;
                    })
                  }
                  className="text-xs px-2 py-1 rounded border border-slate-300"
                >
                  Add bullet
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setArray("recommendationAccordionItems", (next) => next.filter((_, i) => i !== idx))
                  }
                  className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-700"
                >
                  Remove item
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setArray("recommendationAccordionItems", (next) => [
                ...next,
                { title: "", displayOrder: next.length + 1, bullets: [{ text: "", displayOrder: 1 }] },
              ])
            }
            className="text-xs px-3 py-1.5 rounded border border-slate-300"
          >
            Add accordion item
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Sustainability Cards ({lang.toUpperCase()})</p>
          {fields.sustainabilityCards.map((item, idx) => (
            <div key={`sc-${lang}-${idx}`} className="grid sm:grid-cols-12 gap-2 mb-2">
              <input
                type="text"
                value={item.title}
                onChange={(e) =>
                  setArray("sustainabilityCards", (next) => {
                    next[idx] = { ...next[idx], title: e.target.value };
                    return next;
                  })
                }
                placeholder="Title"
                className="sm:col-span-4 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                type="text"
                value={item.text}
                onChange={(e) =>
                  setArray("sustainabilityCards", (next) => {
                    next[idx] = { ...next[idx], text: e.target.value };
                    return next;
                  })
                }
                placeholder="Text"
                className="sm:col-span-4 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                type="text"
                value={item.iconUrl}
                onChange={(e) =>
                  setArray("sustainabilityCards", (next) => {
                    next[idx] = { ...next[idx], iconUrl: e.target.value };
                    return next;
                  })
                }
                placeholder="Icon URL"
                className="sm:col-span-3 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => setArray("sustainabilityCards", (next) => next.filter((_, i) => i !== idx))}
                className="px-3 rounded border border-rose-200 text-rose-700"
              >
                -
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setArray("sustainabilityCards", (next) => [...next, { title: "", text: "", iconUrl: "" }])
            }
            className="text-xs px-3 py-1.5 rounded border border-slate-300"
          >
            Add sustainability card
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">
            Sustainability Storage Items ({lang.toUpperCase()})
          </p>
          {fields.sustainabilityStorageItems.map((item, idx) => (
            <div key={`ssi-${lang}-${idx}`} className="flex gap-2 mb-2">
              <input
                type="text"
                value={item?.text || ""}
                onChange={(e) =>
                  setArray("sustainabilityStorageItems", (next) => {
                    next[idx] = { ...(next[idx] || {}), text: e.target.value };
                    return next;
                  })
                }
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() =>
                  setArray("sustainabilityStorageItems", (next) => next.filter((_, i) => i !== idx))
                }
                className="px-3 rounded border border-rose-200 text-rose-700"
              >
                -
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setArray("sustainabilityStorageItems", (next) => [...next, { text: "" }])}
            className="text-xs px-3 py-1.5 rounded border border-slate-300"
          >
            Add storage item
          </button>
        </div>
      </div>
    );
  };

  const renderLangTabs = (activeLang, setActiveLang, form, keyPrefix) => (
    <div className="flex flex-wrap items-center gap-2">
      {LANGS.map((lang) => {
        const hasContent = Boolean((form.translations?.[lang]?.title || "").trim());
        return (
          <button
            key={`${keyPrefix}-${lang}`}
            type="button"
            onClick={() => setActiveLang(lang)}
            className={activeLang === lang ? langTabActive : langTabIdle}
          >
            {LANG_LABELS[lang]}
            {hasContent ? " ●" : ""}
          </button>
        );
      })}
    </div>
  );

  return (
    <section className="space-y-4">
      <div className="flex justify-end">
        <DashboardReloadButton onClick={loadBoards} loading={loading} label="Reload list" />
      </div>

      <AdminCollapsibleSection
        title="Add new board"
        description="Fill in every language (English, Arabic, Kurdish). Each language is saved together. Fields match the public board detail page."
        badge="Create"
        footer={
          <button type="button" disabled={creating} onClick={handleCreate} className={btnAccent}>
            {creating ? "Saving..." : "Save board (all languages)"}
          </button>
        }
      >
        <div className="grid gap-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700">
              Board Type (shared across languages)
              <input
                type="text"
                value={createForm.boardType}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, boardType: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                placeholder="regular / moisture / fire..."
              />
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700 sm:mt-6">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(createForm.isActive)}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              <span>
                <span className="font-medium">Published on website</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Uncheck to hide this product from the public site (all languages).
                </span>
              </span>
            </label>
            <label className="text-sm text-slate-700 sm:col-span-2">
              Board Image File (shared across languages)
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setCreateImageFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-sm"
              />
              <span className="mt-1 block text-xs text-slate-500">
                Optional. If selected, it uploads automatically after the board is saved.
              </span>
            </label>
          </div>

          {renderLangTabs(activeCreateLang, setActiveCreateLang, createForm, "create")}
          {renderLangContent(createForm, setCreateForm, activeCreateLang)}
        </div>
      </AdminCollapsibleSection>

      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-[#011337]">Your boards</h3>
          {!loading ? (
            <span className="text-sm text-slate-500">
              {groupedBoards.length} item{groupedBoards.length !== 1 ? "s" : ""}
            </span>
          ) : null}
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-slate-200 bg-white">
            Loading boards...
          </p>
        ) : groupedBoards.length ? (
          <div className="grid gap-3">
            {groupedBoards.map((group) => {
              const isActive = group.rows.every((r) => r?.isActive !== false);
              const missing = LANGS.filter((lang) => !group.languages.includes(lang));
              return (
                <AdminProductListCard
                  key={group.key}
                  id={group.rep?.id}
                  title={getBoardName(group.rep)}
                  meta={[
                    group.rep?.boardType ? `Type: ${group.rep.boardType}` : null,
                    `Languages: ${group.languages.map((l) => l.toUpperCase()).join(", ") || "—"}`,
                    missing.length ? `Missing: ${missing.map((l) => l.toUpperCase()).join(", ")}` : null,
                  ].filter(Boolean)}
                  status={isActive}
                  imageUrl={group.rep?.imageUrl}
                  isEditing={editingBoardType === group.key}
                  onEdit={() => startEdit(group)}
                  onDelete={() => handleDelete(group)}
                />
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
            No boards found. Use &quot;Add new board&quot; above to create one.
          </p>
        )}
      </div>

      <AdminEditorModal
        open={Boolean(editingBoardType)}
        title={`Edit board: ${editForm.boardType || editingBoardType || ""}`}
        subtitle="Edit every language, then save. Changes apply to the public product page."
        onClose={closeEdit}
        footer={
          <>
            <button type="button" onClick={closeEdit} className={btnSecondary}>
              Cancel
            </button>
            <button type="button" onClick={saveEdit} disabled={saving} className={btnPrimary}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        }
      >
        <div className="grid gap-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-3">Product image (shared across languages)</p>
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              {editForm.imageUrl ? (
                <img
                  src={resolveMediaUrl(editForm.imageUrl)}
                  alt=""
                  className="w-full sm:w-40 h-28 object-cover rounded-lg border border-slate-200 bg-white"
                />
              ) : (
                <div className="w-full sm:w-40 h-28 rounded-lg border border-dashed border-slate-300 bg-white flex items-center justify-center text-xs text-slate-400">
                  No image
                </div>
              )}
              <div className="flex-1 space-y-2 min-w-0">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEditImageFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm"
                />
                {editImageFile?.name ? (
                  <p className="text-xs text-slate-500">
                    Selected: {editImageFile.name} — uploads when you save changes.
                  </p>
                ) : (
                  <p className="text-xs text-slate-500">Choose a file to replace the current image.</p>
                )}
                {editForm.imageUrl ? (
                  <button
                    type="button"
                    className={btnDanger}
                    onClick={async () => {
                      if (!editRepId) return;
                      try {
                        await removeAdminBoardImage({ id: editRepId });
                        setEditForm((prev) => ({ ...prev, imageUrl: "" }));
                        setEditImageFile(null);
                        await refreshBoardType(editForm.boardType);
                        notify({ type: "success", title: "Image removed" });
                      } catch (error) {
                        notify({
                          type: "error",
                          title: "Remove failed",
                          message: error?.response?.data?.message || error?.message || "",
                        });
                      }
                    }}
                  >
                    Remove image
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700">
              Board Type
              <input
                type="text"
                value={editForm.boardType}
                readOnly
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-slate-100 text-slate-500"
              />
              <span className="mt-1 block text-xs text-slate-400">
                Board type is shared and cannot be changed here.
              </span>
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700 sm:mt-6">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(editForm.isActive)}
                onChange={(e) => setEditForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              <span>
                <span className="font-medium">Published on website</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Uncheck to hide this product from the public site (all languages).
                </span>
              </span>
            </label>
          </div>

          {renderLangTabs(activeEditLang, setActiveEditLang, editForm, "edit")}
          {renderLangContent(editForm, setEditForm, activeEditLang)}

          <AdminProductPdfsPanel
            variant="board"
            productId={editRepId}
            productSlug={String(editForm.boardType ?? "").trim()}
            pdfDocuments={boards.find((x) => Number(x.id) === Number(editRepId))?.pdfDocuments}
            onRefresh={() => refreshBoardType(editForm.boardType)}
          />
        </div>
      </AdminEditorModal>
    </section>
  );
};

export default BoardsManager;
